<script setup lang="ts">
import { useDark } from '@vueuse/core'
import mapboxgl, { Marker } from 'mapbox-gl'
import 'mapbox-gl/dist/mapbox-gl.css'

const props = defineProps<{
  coordinates: [number, number]
  isDraggable?: boolean
  height?: number
  initialZoom?: number
}>()

const emit = defineEmits<{
  'update:coordinates': [value: [number, number]]
}>()

const isDark = useDark()
const mapStyle = computed(
  () => `mapbox://styles/mapbox/${isDark.value ? 'dark-v11' : 'streets-v12'}`,
)
mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_API_KEY

const mapId = `map-${Math.random().toString(36).slice(2, 11)}`
onMounted(() => {
  const map = new mapboxgl.Map({
    container: mapId,
    style: mapStyle.value,
    language: 'de',
    center: props.coordinates,
    zoom: props.initialZoom ?? 9,
  })

  map.addControl(new mapboxgl.FullscreenControl())

  const marker = new Marker({ draggable: props.isDraggable })
    .setLngLat(props.coordinates)
    .addTo(map)
    .on('dragend', (marker) => {
      const lngLat = marker.target.getLngLat()
      emit('update:coordinates', [lngLat.lng, lngLat.lat])
    })

  if (props.isDraggable) {
    map.on('click', (e) => {
      const { lng, lat } = e.lngLat
      marker.setLngLat([lng, lat])
      emit('update:coordinates', [lng, lat])
    })
  }

  watch(
    () => props.coordinates,
    ([lng, lat]) => {
      map.flyTo({ center: [lng, lat], zoom: props.initialZoom ?? 14, essential: true })
    },
  )
})
</script>

<template>
  <div :id="mapId" style="width: 100%" :style="`height: ${height ?? 250}px;`" />
</template>
